import React, { useRef, useState, useEffect } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Menu, Transition } from "@headlessui/react";
import Page from "../components/Page";
import { IconEye, IconFilter, IconPlus, IconReceipt, IconSearch, IconX, IconTrash } from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { toast } from "react-hot-toast";
import { mutate } from "swr";
import { getInvoiceOrders, getInvoicesInit, searchInvoices, useInvoices, voidInvoice } from "../controllers/invoices.controller";
import { getOrdersInit } from "../controllers/orders.controller";
import { CURRENCIES } from "../config/currencies.config";
import { setDetailsForReceiptPrint } from '../helpers/ReceiptHelper';
import { useTheme } from "../contexts/ThemeContext";

const SafeIconPrinter = ({ size = 24, stroke = 2, className = "", ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
  </svg>
);


export default function InvoicesPage() {
  const { t } = useTranslation();
  const filters = [
    { key: "today", value: t("invoices.today") },
    { key: "tomorrow", value: t("invoices.tomorrow") },
    { key: "yesterday", value: t("invoices.yesterday") },
    { key: "last_7days", value: t("invoices.last_7days") },
    { key: "this_month", value: t("invoices.this_month") },
    { key: "last_month", value: t("invoices.last_month") },
    { key: "custom", value: t("invoices.custom") },
  ];

  const searchRef = useRef();
  const fromDateRef = useRef();
  const toDateRef = useRef();
  const filterTypeRef = useRef();
  const voidReasonRef = useRef();
  const { theme } = useTheme();

  const now = new Date();
  const defaultDateFrom = `${now.getFullYear()}-${(now.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;
  const defaultDateTo = `${now.getFullYear()}-${(now.getMonth() + 2)
    .toString()
    .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;

  const customerSearchRef = useRef();

  const [state, setState] = useState({
    search: "",
    searchResults: [],
    spage: 1,
    filter: filters[0].key,
    fromDate: null,
    toDate: null,
    customer: null,

    printSettings: null,
    storeSettings: null,
    currency: null,

    paymentTypes: [],
  });

  const [voidInvoiceId, setVoidInvoiceId] = useState(null);

  useEffect(()=>{
    async function init(){
      try {
        const res = await getInvoicesInit();
        if(res.status == 200) {
          const ordersInit = res.data;
          const currency = CURRENCIES.find((c)=>c.cc==ordersInit?.storeSettings?.currency);

          setState({
            ...state,
            printSettings: ordersInit.printSettings || {},
            storeSettings: ordersInit.storeSettings || {},
            paymentTypes: ordersInit?.paymentTypes || [],
            currency: currency?.symbol,
          });
        }
      } catch (error) {
        console.error(error);
        toast.dismiss();
        toast.error(t('invoices.error_loading_orders'));
      }
    }
    init();
  },[]);

  const {data: invoices, error, isLoading} = useInvoices({
    type: state.filter,
    from: state.filter === "today" ? defaultDateFrom : state.fromDate,
    to: state.filter === "today" ? defaultDateFrom : state.toDate,
  });

  if (isLoading) {
    return <Page>{t('invoices.loading_message')}</Page>;
  }

  if (error) {
    return <Page>{t('invoices.error_loading_details')}</Page>;
  }

  const btnSearch = async () => {
    const searchQuery = searchRef.current.value;
    if (!new String(searchQuery).trim()) {
      return;
    }

    try {
      toast.loading(t('invoices.please_wait'));
      const res = await searchInvoices(new String(searchQuery).trim());
      if(res.status == 200) {
        toast.dismiss();
        setState({
          ...state,
          search: searchQuery,
          searchResults: res.data,
          spage: 1,
        });
      } else {
        toast.dismiss();
        toast.error(t('invoices.no_result_found'));
      }

    } catch (error) {
      console.error(error);
      const message = error.response.data.message || t('invoices.something_went_wrong');

      toast.dismiss();
      toast.error(message);
    }
  }
  const btnClearSearch = () => {
    searchRef.current.value = null;

    setState({
      ...state,
      search: "",
      searchResults: [],
      spage: 1,
    });
  };

  const btnViewReceipt = async (invoiceId, orderIdsArr, tokens, payment_type_id, invoiceObj = null) => {
    try {
      toast.loading(t('invoices.please_wait'));
      const cleanOrderIds = Array.isArray(orderIdsArr)
        ? orderIdsArr.filter(id => id !== null && id !== undefined && String(id).trim() !== '' && !isNaN(id))
        : [];
      const res = await getInvoiceOrders(invoiceId, cleanOrderIds);
      toast.dismiss();

      if(res.status == 200) {
        const {
          subtotal,
          taxTotal,
          serviceChargeTotal,
          total,
          discount,
          roundOff,
          paidAmount,
          dueAmount,
          invoice_no: resInvoiceNo,
          orders: ordersArr = []
        } = res.data;

        const effectiveInvoiceNo = invoiceObj?.invoice_no || resInvoiceNo || invoiceId;

        const orders = [];
        const orderIds = (cleanOrderIds.length > 0 ? cleanOrderIds : (ordersArr.map(o => o.id).filter(Boolean))).join(", ");

        for (const o of ordersArr) {
          const items = o.items || [];
          items.forEach((i)=>{
            const variant = i.variant_id ? {
                id: i.variant_id,
                title: i.variant_title,
                price: i.variant_price
            } : null;
            orders.push({
              ...i,
              title: i.item_title || i.title || "Item",
              addons_ids: i?.addons?.length > 0 ? i?.addons?.map((a)=>a.id || a):[],
              variant: variant
            });
          });
        }

        if (orders.length === 0) {
          orders.push({
            title: `Bill #${effectiveInvoiceNo}`,
            quantity: 1,
            price: Number(subtotal || total || 0)
          });
        }

        const firstOrder = ordersArr[0] || {};
        const customer_id = firstOrder.customer_id || res.data?.customer_id || invoiceObj?.customer_id || null;
        const customer_name = firstOrder.customer_name || res.data?.customer_name || invoiceObj?.name || invoiceObj?.customer_name || 'Walk-in Guest';
        const customer_type = firstOrder.customer_type || invoiceObj?.customer_type || 'CUSTOMER';
        const delivery_type = firstOrder.delivery_type || invoiceObj?.delivery_type || 'dinein';
        const table_title = invoiceObj?.table_title || res.data?.table_title || firstOrder.table_title || firstOrder.room_name || firstOrder.source || null;
        const billedTime = invoiceObj?.created_at;
        const orderedTime = invoiceObj?.order_time || (ordersArr[0] ? ordersArr[0].date : null);

        let paymentMethodText;
        if(payment_type_id) {
          const paymentType = (state.paymentTypes?.find ? state.paymentTypes : []).find((v)=>v.id == payment_type_id);
          if(paymentType) {
            paymentMethodText = paymentType.title;
          }
        }

        const receiptDetails = {
          cartItems: orders,
          deliveryType: delivery_type,
          customerType: customer_type || 'CUSTOMER',
          customer: { id: customer_id, name: customer_name },
          tableId: null,
          table_title: table_title,
          tableName: table_title,
          room_name: firstOrder.room_name || (table_title && table_title.toLowerCase().startsWith('room') ? table_title : null),
          currency: state.currency,
          storeSettings: state.storeSettings,
          printSettings: state.printSettings,
          itemsTotal: subtotal,
          taxTotal: taxTotal,
          serviceChargeTotal: serviceChargeTotal,
          payableTotal: total,
          storedDiscount: discount || 0,
          roundOff: roundOff || 0,
          paidAmount: paidAmount !== undefined ? paidAmount : total,
          dueAmount: dueAmount || 0,
          tokenNo: tokens,
          billedTime: billedTime,
          orderTime: orderedTime,
          orderId: orderIds,
          created_at: billedTime || new Date(),
          date: billedTime || new Date(),
          invoiceId: invoiceId,
          invoice_id: invoiceId,
          billNo: effectiveInvoiceNo,
          invoiceNo: effectiveInvoiceNo,
          paymentMethod: paymentMethodText
        };

        setDetailsForReceiptPrint(receiptDetails);

        if (window.electron) {
          import('../helpers/PrinterHelper').then(({ printReceipt }) => {
            printReceipt({
              ...receiptDetails,
              printSettings: state.printSettings,
              pageFormat: state.printSettings?.page_format || localStorage.getItem('page_format') || '58'
            });
          });
        } else {
          const receiptWindow = window.open("/print-receipt", "_blank", "toolbar=yes,scrollbars=yes,resizable=yes,top=100,left=100,width=450,height=650");
          if (!receiptWindow) {
            import('../helpers/PrinterHelper').then(({ printReceipt }) => {
              printReceipt({
                ...receiptDetails,
                printSettings: state.printSettings,
                pageFormat: state.printSettings?.page_format || localStorage.getItem('page_format') || '58'
              });
            });
          }
        }
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('invoices.error_processing_request');
      toast.dismiss();
      console.error(error);
      toast.error(message);
    }
  };

  const btnPrintReceipt = async (invoiceId, orderIdsArr, tokens, payment_type_id, invoiceObj = null) => {
    try {
      toast.loading(t('invoices.please_wait'));
      const cleanOrderIds = Array.isArray(orderIdsArr)
        ? orderIdsArr.filter(id => id !== null && id !== undefined && String(id).trim() !== '' && !isNaN(id))
        : [];
      const res = await getInvoiceOrders(invoiceId, cleanOrderIds);
      toast.dismiss();

      if(res.status == 200) {
        const {
          subtotal,
          taxTotal,
          serviceChargeTotal,
          total,
          discount,
          roundOff,
          paidAmount,
          dueAmount,
          invoice_no: resInvoiceNo,
          orders: ordersArr = []
        } = res.data;

        const effectiveInvoiceNo = invoiceObj?.invoice_no || resInvoiceNo || invoiceId;

        const orders = [];
        const orderIds = (cleanOrderIds.length > 0 ? cleanOrderIds : (ordersArr.map(o => o.id).filter(Boolean))).join(", ");

        for (const o of ordersArr) {
          const items = o.items || [];
          items.forEach((i)=>{
            const variant = i.variant_id ? {
                id: i.variant_id,
                title: i.variant_title,
                price: i.variant_price
            } : null;
            orders.push({
              ...i,
              title: i.item_title || i.title || "Item",
              addons_ids: i?.addons?.length > 0 ? i?.addons?.map((a)=>a.id || a):[],
              variant: variant
            });
          });
        }

        if (orders.length === 0) {
          orders.push({
            title: `Bill #${effectiveInvoiceNo}`,
            quantity: 1,
            price: Number(subtotal || total || 0)
          });
        }

        const firstOrder = ordersArr[0] || {};
        const customer_id = firstOrder.customer_id || res.data?.customer_id || invoiceObj?.customer_id || null;
        const customer_name = firstOrder.customer_name || res.data?.customer_name || invoiceObj?.name || invoiceObj?.customer_name || 'Walk-in Guest';
        const customer_type = firstOrder.customer_type || invoiceObj?.customer_type || 'CUSTOMER';
        const delivery_type = firstOrder.delivery_type || invoiceObj?.delivery_type || 'dinein';
        const table_title = invoiceObj?.table_title || res.data?.table_title || firstOrder.table_title || firstOrder.room_name || firstOrder.source || null;
        const billedTime = invoiceObj?.created_at;
        const orderedTime = invoiceObj?.order_time || (ordersArr[0] ? ordersArr[0].date : null);

        let paymentMethodText;
        if(payment_type_id) {
          const paymentType = (state.paymentTypes?.find ? state.paymentTypes : []).find((v)=>v.id == payment_type_id);
          if(paymentType) {
            paymentMethodText = paymentType.title;
          }
        }

        const receiptDetails = {
          cartItems: orders,
          deliveryType: delivery_type,
          customerType: customer_type || 'CUSTOMER',
          customer: { id: customer_id, name: customer_name },
          tableId: null,
          table_title: table_title,
          tableName: table_title,
          room_name: firstOrder.room_name || (table_title && table_title.toLowerCase().startsWith('room') ? table_title : null),
          currency: state.currency,
          storeSettings: state.storeSettings,
          printSettings: state.printSettings,
          itemsTotal: subtotal,
          taxTotal: taxTotal,
          serviceChargeTotal: serviceChargeTotal,
          payableTotal: total,
          storedDiscount: discount || 0,
          roundOff: roundOff || 0,
          paidAmount: paidAmount !== undefined ? paidAmount : total,
          dueAmount: dueAmount || 0,
          tokenNo: tokens,
          billedTime: billedTime,
          orderTime: orderedTime,
          orderId: orderIds,
          created_at: billedTime || new Date(),
          date: billedTime || new Date(),
          invoiceId: invoiceId,
          invoice_id: invoiceId,
          billNo: effectiveInvoiceNo,
          invoiceNo: effectiveInvoiceNo,
          paymentMethod: paymentMethodText
        };

        setDetailsForReceiptPrint(receiptDetails);

        import('../helpers/PrinterHelper').then(({ printReceipt }) => {
          printReceipt({
            ...receiptDetails,
            printSettings: state.printSettings,
            pageFormat: state.printSettings?.page_format || localStorage.getItem('page_format') || '58'
          });
        });

      }
    } catch (error) {
      const message = error?.response?.data?.message || t('invoices.error_processing_request');
      toast.dismiss();
      console.error(error);
      toast.error(message);
    }
  };

  const btnVoidInvoice = (invoiceId) => {
    setVoidInvoiceId(invoiceId);
    document.getElementById("void-invoice-dialog").showModal();
  };

  const confirmVoidInvoice = async () => {
    const reason = voidReasonRef.current.value;
    if(!reason) {
      toast.error("Void reason is required!");
      return;
    }

    try {
      toast.loading(t('invoices.please_wait'));
      const res = await voidInvoice(voidInvoiceId, reason);
      toast.dismiss();
      if(res.status == 200) {
        toast.success(res.data.message);
        document.getElementById("void-invoice-dialog").close();
        voidReasonRef.current.value = "";
        mutate(invoices.APIURL);
        // Also clear search result if needed
        if(state.search) {
          btnSearch();
        }
      }
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || t('invoices.something_went_wrong');
      toast.dismiss();
      toast.error(message);
    }
  };

  const displayedInvoices = state.search ? state.searchResults : (invoices || []);
  const totalSales = displayedInvoices.reduce((sum, inv) => sum + Number(inv.total || 0), 0);
  const totalDue = displayedInvoices.reduce((sum, inv) => sum + Number(inv.due_amount || 0), 0);
  const totalDiscount = displayedInvoices.reduce((sum, inv) => sum + Number(inv.discount || 0), 0);
  const totalRoundoff = displayedInvoices.reduce((sum, inv) => sum + Number(inv.round_off || 0), 0);
  const totalComplementary = displayedInvoices.reduce((sum, inv) => sum + Number(inv.complementary_amount || 0), 0);

  return (
    <Page>
      <div className="flex flex-wrap gap-4 flex-col md:flex-row md:items-center md:justify-between mb-4">
        <h3 className="text-2xl">{t('invoices.title')}</h3>

        <div className="flex flex-wrap gap-2">
          <div className='px-2 py-1 rounded-lg flex items-center bg-restro-gray border border-restro-border-green'>
            <input
              ref={searchRef}
              defaultValue={state.search}
              type="text"
              placeholder={t('invoices.search_placeholder')}
              className="bg-transparent placeholder:text-gray-400 outline-none block"
            />
            {state.search && (
              <button onClick={btnClearSearch} className="text-gray-400">
                <IconX stroke={iconStroke} size={18} />
              </button>
            )}
          </div>
          <button
            onClick={btnSearch}
            className='rounded-lg transition active:scale-95 hover:shadow-lg px-3 py-1 text-white ml-2 bg-restro-green hover:bg-restro-green-button-hover'>
            {t('invoices.search')}
          </button>
          <button
            onClick={() => document.getElementById("filter-dialog").showModal()}
            className='w-8 h-8 flex items-center justify-center text-gray-400 rounded-full active:scale-95 transition hover:bg-restro-button-hover'
          >
            <IconFilter />
          </button>
        </div>
      </div>

      {/* Summary Section */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] shadow-sm">
          <p className="text-sm text-gray-500">Total Sales</p>
          <p className="text-xl font-bold text-restro-green">{state.currency}{totalSales.toFixed(2)}</p>
        </div>
        <div className="p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] shadow-sm">
          <p className="text-sm text-gray-500">Due Amount</p>
          <p className="text-xl font-bold text-red-500">{state.currency}{totalDue.toFixed(2)}</p>
        </div>
        <div className="p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] shadow-sm">
          <p className="text-sm text-gray-500">Total Discount</p>
          <p className="text-xl font-bold text-amber-500">{state.currency}{totalDiscount.toFixed(2)}</p>
        </div>
        <div className="p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] shadow-sm">
          <p className="text-sm text-gray-500">Complementary</p>
          <p className="text-xl font-bold text-blue-500">{state.currency}{totalComplementary.toFixed(2)}</p>
        </div>
        <div className="p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] shadow-sm">
          <p className="text-sm text-gray-500">Round Off</p>
          <p className="text-xl font-bold text-gray-700 dark:text-gray-300">{state.currency}{totalRoundoff.toFixed(2)}</p>
        </div>
      </div>

      {/* search result */}
      {state.searchResults.length > 0 && <div className="mt-6">
        <h3>{t('invoices.showing_search_result', { search: state.search })}</h3>
        <div className="overflow-x-auto w-full">
          <table className="table table-sm table-zebra border border-restro-bg-gray w-full">
            <thead>
              <tr>
                <th>{t('invoices.invoice_id')}</th>
                <th>{t('invoices.tokens')}</th>
                <th>Billed Time</th>
                <th>Ordered Time</th>
                <th>{t('invoices.subtotal')}</th>
                <th>{t('invoices.tax')}</th>
                <th>{t('invoices.service_charge')}</th>
                <th>Discount</th>
                <th>Round Off</th>
                <th>{t('invoices.total')}</th>
                <th>{t('invoices.delivery_type')}</th>
                <th>{t('invoices.customer')}</th>
                <th>{t('invoices.table')}/Room</th>
                <th>{t('invoices.action')}</th>
              </tr>
            </thead>
            <tbody>
              {state.searchResults.map((invoice, index)=>{
                const {
                  invoice_id,
                  created_at,
                  order_time,
                  sub_total,
                  tax_total,
                  service_charge_total,
                  total,
                  discount,
                  round_off,
                  table_id,
                  table_title,
                  floor,
                  delivery_type,
                  customer_type,
                  customer_id,
                  name,
                  email,
                  orders,
                  payment_type_id,
                  invoice_payment_status: paymentStatus
                } = invoice;

                const orderIdsArr = (orders || []).map(o=>o.order_id);
                const tokenIdsArr = (orders || []).map(o=>o.token_no);
                const orderIds = orderIdsArr.join(", ");
                const tokens = tokenIdsArr.join(", ");

                return <tr key={index}>
                  <td>{invoice.invoice_no || invoice_id}</td>
                  {/* <td>{orderIds}</td> */}
                  <td>{tokens}</td>
                  <td>{moment(created_at).isValid() ? moment(created_at).format("MMM DD, YYYY, hh:mm A") : (created_at || '-')}</td>
                  <td>{moment(order_time).isValid() ? moment(order_time).format("MMM DD, YYYY, hh:mm A") : (order_time || '-')}</td>
                  <td>{state.currency}{sub_total}</td>
                  <td>{state.currency}{tax_total}</td>
                  <td>
                    {service_charge_total ? `${state.currency || ''}${service_charge_total}` : '-'}
                  </td>
                  <td>{discount > 0 ? `-${state.currency || ''}${discount}` : '-'}</td>
                  <td>{invoice.round_off ? `${state.currency || ''}${invoice.round_off}` : '-'}</td>
                  <td className="font-bold">{state.currency}{total}</td>
                  <td>{delivery_type?delivery_type:"N/A"}</td>
                  <td>{customer_id ?<b>{name}-({customer_id})</b>:"WALKIN"}</td>
                  <td>{table_title ? <b>{table_title}-{floor}</b>:"N/A"}</td>

                  <td className="flex items-center gap-2">
                    <button onClick={()=>{btnViewReceipt(invoice_id, orderIdsArr, tokens, payment_type_id, invoice)}} className="btn btn-sm btn-circle text-restro-text">
                      <IconReceipt stroke={iconStroke} />
                    </button>
                    <button onClick={()=>{btnPrintReceipt(invoice_id, orderIdsArr, tokens, payment_type_id, invoice)}} className="btn btn-sm btn-circle text-restro-text">
                      <SafeIconPrinter stroke={iconStroke} />
                    </button>
                    {paymentStatus !== 'voided' && (
                      <button onClick={() => btnVoidInvoice(invoice_id)} className="btn btn-sm btn-circle text-red-500">
                        <IconTrash stroke={iconStroke} />
                      </button>
                    )}
                  </td>
                </tr>
              })}
            </tbody>
          </table>
        </div>
      </div>}
      {/* search result */}

      {/* data */}
      <h3 className="mt-6 mb-4 text-base">{t('invoices.showing_invoices_for', { filter: filters.find(f => f.key == state.filter).value })}</h3>
      {invoices.length == 0 ? (
        <div className="text-center w-full h-[50vh] flex flex-col items-center justify-center text-gray-500">
          <img
            src="/assets/illustrations/invoice-not-found.webp"
            alt="no invoices"
            className="w-1/2 md:w-60"
          />
          <p className="mt-4">{t('invoices.no_invoices')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto w-full">
          <table className='table table-sm table-zebra border w-full border-restro-border-green'>
            <thead>
              <tr>
                <th>{t('invoices.invoice_id')}</th>
                <th>{t('invoices.tokens')}</th>
                <th>Billed Time</th>
                <th>Ordered Time</th>
                <th>{t('invoices.subtotal')}</th>
                <th>{t('invoices.tax')}</th>
                <th>{t('invoices.service_charge')}</th>
                <th>{t('invoices.discount')}</th>
                <th>Round Off</th>
                <th>{t('invoices.total')}</th>
                <th>{t('invoices.delivery_type')}</th>
                <th>{t('invoices.customer')}</th>
                <th>{t('invoices.table')}/Room</th>
                <th>{t('invoices.action')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice, index)=>{
                const {
                  invoice_id,
                  created_at,
                  order_time,
                  sub_total,
                  tax_total,
                  service_charge_total,
                  total,
                  discount,
                  table_id,
                  table_title,
                  floor,
                  delivery_type,
                  customer_type,
                  customer_id,
                  name,
                  email,
                  orders,
                  payment_type_id,
                  invoice_payment_status: paymentStatus
                } = invoice;

                const orderIdsArr = (orders || []).map(o=>o.order_id);
                const tokenIdsArr = (orders || []).map(o=>o.token_no);
                const orderIds = orderIdsArr.join(", ");
                const tokens = tokenIdsArr.join(", ");

                return <tr key={index}>
                  <td>{invoice.invoice_no || invoice_id}</td>
                  {/* <td>{orderIds}</td> */}
                  <td>{tokens}</td>
                  <td>{moment(created_at).isValid() ? moment(created_at).format("MMM DD, YYYY, hh:mm A") : (created_at || '-')}</td>
                  <td>{moment(order_time).isValid() ? moment(order_time).format("MMM DD, YYYY, hh:mm A") : (order_time || '-')}</td>
                  <td>{state.currency}{sub_total}</td>
                  <td>{state.currency}{tax_total}</td>
                  <td>
                    {service_charge_total ? `${state.currency || ''}${service_charge_total}` : '-'}
                  </td>
                  <td>{discount > 0 ? `-${state.currency || ''}${discount}` : '-'}</td>
                  <td>{invoice.round_off ? `${state.currency || ''}${invoice.round_off}` : '-'}</td>
                  <td className="font-bold">{state.currency}{total}</td>
                  <td>{delivery_type?delivery_type:"N/A"}</td>
                  <td>{customer_id ?<b>{name}-({customer_id})</b>:"WALKIN"}</td>
                  <td>{table_title ? <b>{table_title}-{floor}</b>:"N/A"}</td>

                  <td className="flex items-center gap-2">
                    <button onClick={()=>{btnViewReceipt(invoice_id, orderIdsArr, tokens, payment_type_id, invoice)}} className='btn btn-sm btn-circle text-restro-text hover:bg-restro-button-hover'>
                      <IconReceipt stroke={iconStroke} />
                    </button>
                    <button onClick={()=>{btnPrintReceipt(invoice_id, orderIdsArr, tokens, payment_type_id, invoice)}} className='btn btn-sm btn-circle text-restro-text hover:bg-restro-button-hover'>
                      <SafeIconPrinter stroke={iconStroke} />
                    </button>
                    {paymentStatus !== 'voided' && (
                      <button onClick={() => btnVoidInvoice(invoice_id)} className='btn btn-sm btn-circle text-red-500 hover:bg-restro-button-hover'>
                        <IconTrash stroke={iconStroke} />
                      </button>
                    )}
                  </td>
                </tr>
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* data */}

      {/* filter dialog */}
      <dialog id="filter-dialog" className="modal">
        <div className='modal-box border border-restro-border-green dark:rounded-2xl'>
          <h3 className="font-bold text-lg flex items-center">
            <IconFilter stroke={iconStroke} /> {t('invoices.filter')}
          </h3>
          {/* filters */}
          <div className="my-4">
            <div>
              <label className=" block text-gray-500 text-sm">{t('invoices.filter')}</label>
              <select
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'
                ref={filterTypeRef}
              >
                {filters.map((filter, index) => (
                  <option key={index} value={filter.key}>
                    {filter.value}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 mt-4">
              <div className="flex-1">
                <label
                  htmlFor="fromDate"
                  className=" block text-gray-500 text-sm"
                >
                  {t('invoices.from')}
                </label>
                <input
                  defaultValue={defaultDateFrom}
                  type="date"
                  ref={fromDateRef}
                  className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor="toDate"
                  className=" block text-gray-500 text-sm"
                >
                  {t('invoices.to')}
                </label>
                <input
                  defaultValue={defaultDateTo}
                  type="date"
                  ref={toDateRef}
                  className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'
                />
              </div>
            </div>
          </div>
          {/* filters */}
          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className='btn transition active:scale-95 hover:shadow-lg px-4 py-3 flex-1 items-center justify-center align-center rounded-xl border border-restro-border-green bg-restro-card-bg hover:bg-restro-button-hover text-restro-text'>{t('invoices.close')}</button>
              <button onClick={()=>{
                setState({
                  ...state,
                  filter: filterTypeRef.current.value,
                  fromDate: fromDateRef.current.value || null,
                  toDate: toDateRef.current.value || null,
                });
              }}  className='rounded-xl transition active:scale-95 hover:shadow-lg px-4 py-3 text-white ml-3 border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover'>{t('invoices.apply')}</button>
            </form>
          </div>
        </div>
      </dialog>
      {/* filter dialog */}

      {/* void invoice dialog */}
      <dialog id="void-invoice-dialog" className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg">Void Invoice</h3>
          <p className="py-4 text-sm text-gray-500">Are you sure you want to void this invoice? This will reverse payments and reset orders to pending.</p>
          <div className="form-control">
            <label className="label">
              <span className="label-text">Reason for voiding:</span>
            </label>
            <textarea
              ref={voidReasonRef}
              className="textarea textarea-bordered h-24"
              placeholder="e.g. Mistake in order, customer cancelled, etc."
            ></textarea>
          </div>
          <div className="modal-action">
            <form method="dialog">
              <button className="btn mr-2">Cancel</button>
              <button onClick={(e) => { e.preventDefault(); confirmVoidInvoice(); }} className="btn btn-error text-white">Confirm Void</button>
            </form>
          </div>
        </div>
      </dialog>
      {/* void invoice dialog */}
    </Page>
  )
}
